import React, {useContext, useMemo, useRef, useState} from "react"
import {Button, Checkbox} from "antd"
import cx from "classnames"
import PerfectScrollbar from "react-perfect-scrollbar"
import {Icon} from "components/Icon"
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd"
import {KlassappTableProps} from "types/common"
import {checkIsFirefox} from "helpers"
import {TableRowItem, EmptyRow, LoadingRow, ColumnPicker} from "./parts"
import styles from "./KlassappTable.module.css"
import {useSaveScroll} from "./useSaveScroll"
import {BasePopupTableScrollContext} from "components/popup/BasePopupTableScrollContext"

const reorder = (list: any[], startIndex: number, endIndex: number): any[] => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
}

function BodyTable(props) {
    const {tableData, isLoading, columns, isDraggable, classNameActionMenu, keyRender, emptyText, ...rest} = props

    if (isLoading) {
        return (
            <tbody className={styles.bodyTable}>
                <LoadingRow colSpan={columns.length + 2} />
            </tbody>
        )
    }
    if (!tableData.length) {
        return (
            <tbody className={styles.bodyTable}>
                <EmptyRow colSpan={columns.length + 2} emptyText={emptyText} />
            </tbody>
        )
    }

    if (isDraggable) {
        return (
            <Droppable droppableId="table">
                {(droppableProvided) => (
                    <tbody
                        ref={droppableProvided.innerRef}
                        className={styles.bodyTable}
                        {...droppableProvided.droppableProps}>
                        {tableData.map((item, index: number) => (
                            <Draggable draggableId={`${item[keyRender]}`} index={index} key={item[keyRender]}>
                                {(provided, snapshot) => (
                                    <TableRowItem
                                        provided={provided}
                                        snapshot={snapshot}
                                        key={item[keyRender]}
                                        data={item}
                                        columns={columns}
                                        isDraggable={isDraggable}
                                        keyRender={keyRender}
                                        {...rest}
                                    />
                                )}
                            </Draggable>
                        ))}
                        {droppableProvided.placeholder}
                    </tbody>
                )}
            </Droppable>
        )
    }
    return (
        <tbody className={styles.bodyTable}>
            {tableData.map((item) => (
                <TableRowItem
                    classNameActionMenu={classNameActionMenu}
                    key={item[keyRender]}
                    keyRender={keyRender}
                    data={item}
                    columns={columns}
                    isDraggable={isDraggable}
                    {...rest}
                />
            ))}
        </tbody>
    )
}

export function KlassappTable(props: KlassappTableProps) {
    const {
        className,
        tableClassName,
        tableRowParent,
        isShowCheckedColumnHeader = true,
        onToggleCheckBox,
        parentColumns = [],
        subHeaderColumns = [],
        columns,
        menuActions,
        data,
        isLoading,
        fields,
        allFields,
        unselectableFields,
        isShowCheckedColumn,
        isShowColumnPicker,
        isShowHeader = true,
        isShowSubHeader = false,
        subHeaderClassName,
        isDraggable,
        orderField,
        onClickRowItem,
        onDoubleClickRowItem,
        classNameActionMenu,
        onChangeFields,
        onChangeAllFields,
        onDraggableColumn,
        onUpdateTableData,
        onUpdateRowData,
        onToggleCheckHeader,
        onClickSortColumn,
        onDraggableEnd,
        icon,
        expandable,
        keyRender = "id",
        colSpanAll,
        emptyText,
        expandableKey,
        defaultExpandable,
        expandIndent = 24,
        tableRef
    } = props
    const filterColumns = columns.filter(Boolean)

    const preferNativeTableScroll = useContext(BasePopupTableScrollContext)

    const colsPickerRef = useRef(null)
    const [isShowScrollTable, setIsShowScrollTable] = useState(true)
    const [isShowChooseCols, setIsShowChooseCols] = useState(false)
    const {handleActionRow} = useSaveScroll(tableRef)

    const fixedColumnLeftMap = useMemo(() => {
        const map: Record<number, number> = {}
        let left = isShowCheckedColumn ? 32 : 0
        for (let i = 0; i < filterColumns.length; i++) {
            const column = filterColumns[i]
            if (!column.isFixed) continue
            if (fields.includes(column.title)) {
                map[i] = left
                left += parseInt(column.style?.minWidth ?? "0", 10) || 0
            }
        }
        return map
    }, [filterColumns, fields, isShowCheckedColumn])

    const hasFixedColumns = Object.keys(fixedColumnLeftMap).length > 0

    const lastFixedColumnIndex = useMemo(() => {
        const keys = Object.keys(fixedColumnLeftMap)
        if (!keys.length) return -1
        return Math.max(...keys.map(Number))
    }, [fixedColumnLeftMap])

    const isChecked = useMemo(() => {
        const selectableRows = data.filter((row) => !row?.isCheckboxDisabled && !row?.isCheckboxLocked)
        return selectableRows.length > 0 && selectableRows.every((row) => !!row?.isChecked)
    }, [data])

    const onToggleCheck = (checked) => {
        if (onToggleCheckHeader) {
            onToggleCheckHeader(checked)
        } else {
            const newTableData = data.map((item) => {
                item.isChecked = checked
                return item
            })
            onUpdateTableData(newTableData)
        }
    }

    const onDragEnd = (result) => {
        const {destination, source} = result || {}
        if (!destination || destination.index === source.index) {
            return
        }
        if (destination.index === source.index) {
            return
        }
        if (onDraggableEnd) {
            onDraggableEnd(result)
        } else {
            const newTableData = reorder(data, source.index, destination.index)
            onUpdateTableData(newTableData)
        }
    }

    const onShowChooseCols = () => {
        setIsShowChooseCols(true)
    }

    const renderOrderIcon = (field) => {
        if (!orderField || orderField.field !== field) {
            return null
        }
        return orderField.order === "asc" ? (
            <Icon icon="SORT_DOWN_SOLID" className={styles.sortIcon} />
        ) : (
            <Icon icon="SORT_UP_SOLID" className={styles.sortIcon} />
        )
    }

    const renderTableContent = () => {
        return (
            <table className={cx(styles.table, tableClassName)}>
                {isShowHeader && (
                    <thead className={styles.tableHead}>
                        {parentColumns.length ? (
                            <tr className={tableRowParent}>
                                {isShowCheckedColumn && (
                                    <th className={`${styles.textLeft} ${styles.tableCellHeader}`}></th>
                                )}
                                {!!expandable?.expandedRowRender && !expandable?.expandColumnSequence && (
                                    <th className={`${styles.textLeft} ${styles.tableCellHeader}`}></th>
                                )}
                                {parentColumns.map((col, index) => (
                                    <th
                                        key={
                                            col.field ??
                                            col.key ??
                                            `parent-col-${index}-${col.span}-${String(col.title)}`
                                        }
                                        className={`${styles.textLeft} ${styles.tableCellHeader} ${col.classNameHeader}`}
                                        colSpan={col.span}>
                                        {col.title}
                                    </th>
                                ))}
                                {isShowColumnPicker ? (
                                    <th className={`${styles.textLeft} ${styles.tableCellHeader}`}></th>
                                ) : menuActions && menuActions.length ? (
                                    <th className={`${styles.textLeft} ${styles.tableCellHeader}`}></th>
                                ) : null}
                            </tr>
                        ) : null}
                        <tr className={styles.tableRowHeader}>
                            {isShowCheckedColumn && (
                                <th
                                    className={cx(
                                        styles.tableCell,
                                        styles.tableCellHeader,
                                        styles.expandIconCell,
                                        hasFixedColumns ? styles.stickyHeaderCell : ""
                                    )}
                                    style={hasFixedColumns ? {left: 0} : undefined}>
                                    {isShowCheckedColumnHeader && (
                                        <Checkbox
                                            checked={isChecked}
                                            onChange={(event) => onToggleCheck(event.target.checked)}
                                        />
                                    )}
                                </th>
                            )}
                            {!!expandable?.expandedRowRender && !expandable?.expandColumnSequence && (
                                <th
                                    className={`${styles.tableCell} ${styles.tableCellHeader} ${styles.expandIconCell}`}></th>
                            )}
                            {filterColumns.map((column, index) => {
                                const canSortable = column.sortable && onClickSortColumn
                                const orderField = column.orderField || column.field
                                const title = column.titleHtml || column.title
                                const isSticky = fixedColumnLeftMap[index] !== undefined
                                const isLastSticky = index === lastFixedColumnIndex
                                return (
                                    fields.includes(column.title) && (
                                        <th
                                            key={index}
                                            className={cx(
                                                styles.tableCell,
                                                styles.tableCellHeader,
                                                column.classNameHeader,
                                                canSortable ? styles.cursor : "",
                                                isSticky ? styles.stickyHeaderCell : ""
                                            )}
                                            style={{
                                                ...column.headerStyle,
                                                ...(isSticky && {left: fixedColumnLeftMap[index]}),
                                                ...(isLastSticky && {borderRight: "2px solid #d0d0d0"})
                                            }}
                                            onClick={canSortable ? () => onClickSortColumn(orderField) : () => {}}>
                                            {!column.isHideTitle && title}
                                            {canSortable && renderOrderIcon(orderField)}
                                        </th>
                                    )
                                )
                            })}
                            {isShowColumnPicker ? (
                                <th className={`${styles.tableCell} ${styles.tableCellHeader}`}>
                                    <div className={styles.flexEnd}>
                                        <Button
                                            className={styles.plusIconBtn}
                                            icon={<Icon icon={icon || "COLUMNS"} className={styles.iconPlus} />}
                                            onClick={onShowChooseCols}
                                        />
                                    </div>
                                </th>
                            ) : menuActions && menuActions.length ? (
                                <th
                                    className={`${styles.tableCell} ${styles.tableCellHeader} ${classNameActionMenu}`}></th>
                            ) : null}
                        </tr>
                    </thead>
                )}

                {isShowSubHeader && (
                    <thead className={cx(styles.tableSubHead, subHeaderClassName)}>
                        <tr className={styles.tableRowHeader}>
                            <th
                                className={`${styles.tableCell} ${styles.tableCellSubHeader} ${styles.expandIconCell}`}></th>
                            {subHeaderColumns.map((column, index) => {
                                const canSortable = column.sortable && onClickSortColumn
                                const orderField = column.orderField || column.field

                                return (
                                    <th
                                        key={index}
                                        className={cx(
                                            styles.tableCell,
                                            styles.tableCellSubHeader,
                                            column.classNameHeader,
                                            canSortable ? styles.cursor : ""
                                        )}
                                        style={column.headerStyle}
                                        onClick={canSortable ? () => onClickSortColumn(orderField) : () => {}}>
                                        {column.titleHtml || column.title}
                                        {canSortable && renderOrderIcon(orderField)}
                                    </th>
                                )
                            })}
                            {isShowColumnPicker ? null : menuActions && menuActions.length ? (
                                <th
                                    className={`${styles.tableCell} ${styles.tableCellSubHeader} ${classNameActionMenu}`}></th>
                            ) : null}
                        </tr>
                    </thead>
                )}
                <BodyTable
                    columns={filterColumns}
                    tableData={data}
                    menuActions={menuActions}
                    classNameActionMenu={classNameActionMenu}
                    fields={fields}
                    isLoading={isLoading}
                    isShowCheckedColumn={isShowCheckedColumn}
                    isShowColumnPicker={isShowColumnPicker}
                    onClickRowItem={onClickRowItem}
                    onToggleCheckBox={onToggleCheckBox}
                    onDoubleClickRowItem={onDoubleClickRowItem}
                    onUpdateRowData={onUpdateRowData}
                    isDraggable={isDraggable}
                    expandable={expandable}
                    colSpanAll={colSpanAll}
                    keyRender={keyRender}
                    expandableKey={expandableKey}
                    defaultExpandable={defaultExpandable}
                    expandIndent={expandIndent}
                    emptyText={emptyText}
                    handleActionRow={handleActionRow}
                    fixedColumnLeftMap={fixedColumnLeftMap}
                    hasFixedColumns={hasFixedColumns}
                    lastFixedColumnIndex={lastFixedColumnIndex}
                />
            </table>
        )
    }

    const renderTable = () => {
        const isFirefox = checkIsFirefox()
        const useNativeScrollHost = preferNativeTableScroll || isFirefox
        const nativeScrollClassName = preferNativeTableScroll
            ? styles.tableInModalNativeScroll
            : styles.tableFirefoxScroll
        return (
            <div className={cx(styles.root, {"hidden-scroll": !isShowScrollTable})}>
                {useNativeScrollHost ? (
                    <div
                        ref={tableRef}
                        className={cx(
                            "custom-scroll",
                            "klassapp-table",
                            isShowScrollTable ? nativeScrollClassName : styles.tableScrollHidden,
                            className
                        )}>
                        {renderTableContent()}
                    </div>
                ) : (
                    <PerfectScrollbar
                        ref={tableRef}
                        className={cx(
                            "custom-scroll",
                            "klassapp-table",
                            isShowScrollTable ? styles.tableScroll : styles.tableScrollHidden,
                            className
                        )}>
                        {renderTableContent()}
                    </PerfectScrollbar>
                )}
                {isShowChooseCols && (
                    <div ref={colsPickerRef}>
                        <ColumnPicker
                            fields={fields}
                            allFields={allFields}
                            unselectableFields={unselectableFields}
                            onChangeFields={onChangeFields}
                            onChangeAllFields={onChangeAllFields}
                            setIsShowChooseCols={setIsShowChooseCols}
                            onDraggableColumn={onDraggableColumn}
                        />
                    </div>
                )}
            </div>
        )
    }

    if (isDraggable) {
        return (
            <DragDropContext onDragEnd={onDragEnd} style={{overflow: "auto"}}>
                {renderTable()}
            </DragDropContext>
        )
    }

    return renderTable()
}
